<template>
  <div ref="galleryRef">
    <a
      ref="anchorRef"
      :href="fullSrc"
      :data-pswp-width="resolvedWidth || undefined"
      :data-pswp-height="resolvedHeight || undefined"
      target="_blank"
      rel="noreferrer noopener"
    >
      <img :src="src" :alt="alt || ''" :class="imgClass" class="max-w-full" />
    </a>
  </div>
  <!-- PhotoSwipe root is injected automatically by the library -->
  <!-- We only provide a gallery container with a single anchor child. -->
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watchEffect } from 'vue';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

const props = defineProps<{
  src: string;
  alt?: string;
  /**
   * Optional high-res image. Falls back to `src` if not provided.
   */
  fullSrc?: string;
  /**
   * Known natural width/height. If not provided, we detect them.
   */
  width?: number;
  height?: number;
  /**
   * Extra classes for the <img> element.
   */
  imgClass?: string;
}>();

const galleryRef = ref<HTMLElement | null>(null);
const anchorRef = ref<HTMLAnchorElement | null>(null);

const resolvedWidth = ref<number | null>(props.width ?? null);
const resolvedHeight = ref<number | null>(props.height ?? null);
const fullSrc = ref<string>(props.fullSrc || props.src);

let lightbox: PhotoSwipeLightbox | null = null;

const ensureImageNaturalSize = async () => {
  if (resolvedWidth.value && resolvedHeight.value) return;
  await new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolvedWidth.value = img.naturalWidth || null;
      resolvedHeight.value = img.naturalHeight || null;
      resolve();
    };
    img.onerror = () => resolve();
    img.src = fullSrc.value;
    if (img.complete) {
      resolvedWidth.value = img.naturalWidth || null;
      resolvedHeight.value = img.naturalHeight || null;
      resolve();
    }
  });
};

const initLightbox = () => {
  if (!galleryRef.value) return;
  if (lightbox) return;
  lightbox = new PhotoSwipeLightbox({
    gallery: galleryRef.value,
    children: 'a',
    pswpModule: () => import('photoswipe'),
    wheelToZoom: true,
    showHideAnimationType: 'zoom',
    returnFocus: false, // causes a weird maximum call stack size exceeded bug on IOS
    trapFocus: false, // causes a weird maximum call stack size exceeded bug on IOS
  });
  lightbox.init();
};

onMounted(async () => {
  await ensureImageNaturalSize();
  initLightbox();
});

onBeforeUnmount(() => {
  if (lightbox) {
    lightbox.destroy();
    lightbox = null;
  }
});

watchEffect(() => {
  fullSrc.value = props.fullSrc || props.src;
});
</script>
